import { forwardRef } from "react";
import { TabButtonProps } from "./TabItemProps";
import { TabsTrigger } from "@radix-ui/react-tabs";
import { cva, VariantProps } from "class-variance-authority";
import { cn } from "@/lib";

const courseTabButtonVariant = cva(
  "relative font-semibold fs-15 w-full bg-transparent cursor-pointer box-border max-w-full peer/tab",
  {
    variants: {
      showNumber: {
        true: "p-7.5",
        false: "py-3.5 px-7.5",
      },
      variant: {
        outlined:
          "group-hover/tab:border-primary data-[state=active]:border-primary rounded border-2 border-primary-light",
        text: "border-none capitalize text-start whitespace-nowrap flex-1",
      },
    },
    compoundVariants: [
      {
        showNumber: true,
        variant: "text",
        className: "px-2 py-1",
      },
    ],
  },
);

const CourseTabButton = forwardRef<
  HTMLButtonElement,
  TabButtonProps & VariantProps<typeof courseTabButtonVariant>
>(
  (
    { variant, showNumber, ...props }: TabButtonProps & VariantProps<typeof courseTabButtonVariant>,
    ref,
  ) => {
    return (
      <TabsTrigger
        {...props}
        className={cn(courseTabButtonVariant({ variant, showNumber }), props.className)}
        ref={ref}
      />
    );
  },
);

/**
 * Used to create a pseudo tab button during renaming,
 * to avoid html issues that can be caused by putting an input inside of a button
 */
const PseudoTabButton = ({
  variant,
  showNumber,
  ...props
}: Pick<TabButtonProps, "variant" | "showNumber" | "className" | "style" | "children">) => {
  return (
    <div
      {...props}
      className={cn(courseTabButtonVariant({ variant, showNumber }), props.className)}
    />
  );
};

export default CourseTabButton;
export { CourseTabButton, PseudoTabButton };
